import type { Material, Object3D } from 'three';

const disabledRaycast = () => {};

/** Shows or hides an object and syncs raycast with visibility. */
export function applyObjectVisibility(object: Object3D, isVisible: boolean): void {
  object.visible = isVisible;
  object.traverse((child: Object3D) => {
    if (isVisible) {
      if (child.raycast === disabledRaycast) {
        child.raycast = Object.getPrototypeOf(child).raycast;
      }
      return;
    }
    child.raycast = disabledRaycast;
  });
}

type MaterialWithOpacity = Material & { opacity: number; transparent: boolean };

function isMaterialWithOpacity(material: Material): material is MaterialWithOpacity {
  return typeof (material as MaterialWithOpacity).opacity === 'number';
}

function collectMaterialsFromObject(object: Object3D): MaterialWithOpacity[] {
  const materials: MaterialWithOpacity[] = [];

  object.traverse((child: Object3D) => {
    const meshMaterial = (child as Object3D & { material?: Material | Material[] }).material;
    if (!meshMaterial) return;

    const list = Array.isArray(meshMaterial) ? meshMaterial : [meshMaterial];
    for (const material of list) {
      if (!isMaterialWithOpacity(material)) continue;
      if (materials.includes(material)) continue;
      materials.push(material);
    }
  });

  return materials;
}

/** Collects unique materials with opacity under an object subtree. */
export function collectFadeMaterials(object: Object3D): MaterialWithOpacity[] {
  return collectMaterialsFromObject(object);
}

/** Enables transparency and sets opacity on all collected materials. */
export function setMaterialsOpacity(materials: MaterialWithOpacity[], opacity: number): void {
  for (const material of materials) {
    material.transparent = true;
    material.opacity = opacity;
  }
}
